import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

import {
  Button,
  InputMask,
  Select,
  TextField,
  Textarea,
  inputSizeClasses,
  inputBaseClasses,
} from 'src/ui/components';

import { usePersonRepoStore } from 'src/features/PersonRepository/store/personRepoStore';
import { useValidateUrl } from 'src/features/PersonRepository/api';

import { ResponseType } from 'src/features/PersonRepository/types/QuestionsTabType';
import { validateEmail } from 'src/features/AddEditUsers/utils/userHelper';

export const textType = {
  memo: 1,
  text: 0,
};

export const urlType = [
  { value: 'https://', label: 'https://' },
  { value: 'http://', label: 'http://' },
];

export function TextEdit({
  question,
}: {
  question: ResponseType;
}): ReactElement {
  const [btnState, setButtonState] = useState(false);
  const [urlOption, setUrlOption] = useState('https://');
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);

  const qnTempId = useMemo(() => {
    return question?.TemplateId || 0;
  }, [question]);

  const updateTextView = usePersonRepoStore(
    useCallback(state => state?.updateTextView, []),
  );
  const textTypeFormat = usePersonRepoStore(
    useCallback(state => state?.textTypeFormat, []),
  );

  const textValue = usePersonRepoStore(
    useCallback(
      state => state?.formValues.Poc.TextView?.[qnTempId],
      [qnTempId],
    ),
  );

  const setShowTextValidationErr = usePersonRepoStore(
    React.useCallback(state => state.setShowTextValidationErr, []),
  );

  const showTextValidationErr = usePersonRepoStore(
    React.useCallback(state => state.showTextValidationErr, []),
  );

  /*disable the save button if validation is thrown in FE */
  const setShowEmailValidation = usePersonRepoStore(
    React.useCallback(state => state.setShowEmailValidation, []),
  );

  /*disable the save button if validation is thrown in FE */
  const setShowUrlValidation = usePersonRepoStore(
    React.useCallback(state => state.setShowUrlValidation, []),
  );

  // to get question description
  const questionDescription = useMemo(() => {
    const formatId = question.TextTypeOptions.TextQuestionTypeFormatId;
    const description = textTypeFormat?.find(
      item => item?.Id === formatId,
    )?.Description;

    return description;
  }, [question.TextTypeOptions?.TextQuestionTypeFormatId, textTypeFormat]);

  const questionPattern = useMemo(() => {
    const formatId = question.TextTypeOptions?.TextQuestionTypeFormatId;
    const pattern = textTypeFormat?.find(
      item => item?.Id === formatId,
    )?.Pattern;

    return pattern;
  }, [question.TextTypeOptions?.TextQuestionTypeFormatId, textTypeFormat]);

  //getting the text value object according to th etemplate id
  const textvalueObject = useMemo(() => {
    if (textValue !== undefined) {
      let textVal = textValue;
      let textFormattedValue;

      if (questionDescription === 'URL' && textVal?.startsWith('https://')) {
        textFormattedValue = textVal?.replace('https://', '').trim();
      } else if (
        questionDescription === 'URL' &&
        textVal?.startsWith('http://')
      ) {
        textFormattedValue = textVal?.replace('http://', '').trim();
      } else {
        textFormattedValue = textVal;
      }

      return textFormattedValue;
    }
  }, [questionDescription, textValue]);

  const titleValue = useMemo(() => {
    if (questionDescription !== 'Email' || !textvalueObject) return '';
    const email = textvalueObject.trim();
    if (!email.includes('@')) {
      return `Please include an '@' in the email address. '${email}' is missing an '@'.`;
    }
    if (email.endsWith('@')) {
      return `Please enter a part following '@'. '${email}' is incomplete.`;
    }
    return '';
  }, [questionDescription, textvalueObject]);

  const url = useMemo(() => {
    if (
      questionDescription == 'URL' &&
      textvalueObject !== '' &&
      textvalueObject != undefined
    ) {
      return urlOption + textvalueObject;
    }
  }, [questionDescription, textvalueObject, urlOption]);

  const {
    data: result,
    refetch: validateUrl,
    isRefetching,
    isLoading,
  } = useValidateUrl(url);

  const invalidErrorText = useCallback(
    (text: string) => {
      if (result && result?.Data?.IsAllowedByDB) return 'Validated by policy';
      else {
        if (text === 'Invalid') {
          setShowUrlValidation(true);
          setShowEmailValidation(false);
        } else {
          setShowUrlValidation(false);
          setShowEmailValidation(false);
        }
        return text;
      }
    },
    [result, setShowEmailValidation, setShowUrlValidation],
  );

  const errorMessage = useMemo(() => {
    if (questionDescription === 'URL') {
      if (
        result &&
        (result?.StatusCode === 200 || result?.StatusCode === 202) &&
        result?.Data?.StatusDescription === 'OK' &&
        textvalueObject !== '' &&
        !isRefetching &&
        !isLoading &&
        btnState
      )
        return invalidErrorText('Valid');
      else if (
        textvalueObject !== '' &&
        !isRefetching &&
        btnState &&
        !isLoading
      )
        return invalidErrorText('Invalid');
      else {
        setShowUrlValidation(false);
        setShowEmailValidation(false);
        return '';
      }
    }
  }, [
    questionDescription,
    result,
    textvalueObject,
    isRefetching,
    isLoading,
    btnState,
    invalidErrorText,
    setShowUrlValidation,
    setShowEmailValidation,
  ]);

  //setting url with https , https
  useEffect(() => {
    if (textvalueObject && questionDescription === 'URL') {
      if (textvalueObject.startsWith('http://')) {
        setUrlOption('http://');
      } else if (textvalueObject.startsWith('https://')) {
        setUrlOption('https://');
      }
    }
  }, [questionDescription, textvalueObject]);

  //checking it is Email or not
  const isEmail = useMemo(() => {
    if (
      question.TextTypeOptions?.Type === textType.text &&
      questionDescription === 'Email'
    ) {
      return true;
    } else {
      return false;
    }
  }, [question.TextTypeOptions?.Type, questionDescription]);

  const textContentType = useMemo(() => {
    if (
      question.TextTypeOptions?.Type === textType.text &&
      questionDescription === 'Numbers Only'
    ) {
      return 'number';
    } else {
      return 'text';
    }
  }, [question.TextTypeOptions?.Type, questionDescription]);

  const maskPattern = useMemo(() => {
    if (question.TextTypeOptions?.Type === textType.text) {
      switch (questionDescription) {
        case 'SSN':
          return questionPattern;
        case 'Military Time':
          return '99:99';
        case 'Telephone (US)':
          return '999-999-9999 **********';
        default:
          return '';
      }
    }
  }, [question.TextTypeOptions?.Type, questionDescription, questionPattern]);

  const onHandleTestLink = () => {
    validateUrl();
    setButtonState(true);
  };

  useEffect(() => {
    if (textvalueObject && questionDescription === 'URL') {
      setButtonState(false);
    }
  }, [textvalueObject, questionDescription]);

  const validationError: string = useMemo(() => {
    let validationMsg: string = '';
    if (textvalueObject && textvalueObject.length !== 0 && isEmail) {
      if (!validateEmail(textvalueObject)) {
        setShowEmailValidation(true);
        validationMsg = 'Invalid email';
      } else {
        setShowEmailValidation(false);
      }
    } else {
      setShowEmailValidation(false);
    }

    return validationMsg;
  }, [isEmail, setShowEmailValidation, textvalueObject]);

  const onUrlTextHandler = (e: any) => {
    let urlValue = '';
    if (e?.startsWith('https://')) {
      setUrlOption('https://');
      urlValue = e;
    } else if (e?.startsWith('http://')) {
      setUrlOption('http://');
      urlValue = e;
    } else if (e !== '') urlValue = urlOption + e;
    updateTextView(urlValue, qnTempId);
  };

  //function to handle military time onChange validation
  const handleOnChangeMilitartyTime = (
    inputValue: string,
    currentValue: string,
    updateTextView: any,
    qnTempId: any,
  ) => {
    const digitsOnly = inputValue.replace(/\D/g, '').slice(0, 4);
    const prev = currentValue.replace(':', '');

    if (digitsOnly.length < prev.length) {
      const formatted =
        digitsOnly.length > 2
          ? digitsOnly.slice(0, 2) + ':' + digitsOnly.slice(2)
          : digitsOnly;
      updateTextView(formatted, qnTempId);
      return;
    }

    const len = digitsOnly.length;
    if (len === 1 && parseInt(digitsOnly[0]) > 2) return;
    if (len === 2) {
      const h1 = parseInt(digitsOnly[0]);
      const h2 = parseInt(digitsOnly[1]);
      if ((h1 === 2 && h2 > 3) || isNaN(h2)) return;
    }
    if (len === 3 && parseInt(digitsOnly[2]) > 5) return;
    if (len === 4 && isNaN(parseInt(digitsOnly[3]))) return;

    const formatted =
      len > 2 ? digitsOnly.slice(0, 2) + ':' + digitsOnly.slice(2) : digitsOnly;

    updateTextView(formatted, qnTempId);
  };

  return (
    <dd className="flex gap-2  border-b dark:border-dark-medium">
      <h4 className="w-1/3 px-3 py-2 text-sm font-medium">
        {question?.FieldLabel}
      </h4>
      {question.TextTypeOptions?.Type == textType.memo ? (
        <p className="w-4/6 px-3 py-2 text-sm">
          <Textarea
            id={`Text.${qnTempId}`}
            rows={5}
            value={textvalueObject}
            onChange={e => {
              updateTextView(e.target.value, qnTempId);
            }}
          />
        </p>
      ) : isEmail ? (
        <p className="w-4/6 px-3 py-2 text-sm">
          <TextField
            id={`Text.${qnTempId}`}
            value={textvalueObject}
            onChange={e => {
              updateTextView(e.target.value, qnTempId);
              if (showTextValidationErr) {
                setShowTextValidationErr(false);
              }
            }}
            title={titleValue}
            validationError={validationError}
          />
        </p>
      ) : questionDescription === 'Military Time' ? (
        <p className="w-4/6 px-3 py-2 text-sm">
          <TextField
            id={`Text.${qnTempId}`}
            // type="text"
            // inputMode="numeric"
            maxLength={5}
            placeholder="__:__"
            value={textvalueObject}
            onChange={e =>
              handleOnChangeMilitartyTime(
                e.target.value,
                textvalueObject,
                updateTextView,
                qnTempId,
              )
            }
            onPaste={e => {
              e.preventDefault();
            }}
            onDrop={e => {
              e.preventDefault();
            }}
            onDragOver={e => {
              e.preventDefault();
            }}
            onBlur={() => {
              const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
              if (!regex.test(textvalueObject)) {
                updateTextView('', qnTempId);
              }
            }}
          />
        </p>
      ) : questionDescription === 'URL' ? (
        <p className="flex w-4/6 items-start gap-3 px-3 py-2 text-sm">
          <Select
            id="textType"
            options={urlType}
            value={urlOption}
            onChange={(e: any) => {
              setUrlOption(e.target.value);
              setButtonState(false);
              textvalueObject !== ''
                ? updateTextView(e.target.value + textvalueObject, qnTempId)
                : '';
            }}
          />
          <div className="grow">
            <TextField
              id={`Text.${qnTempId}`}
              type={textContentType}
              maxlength={1000}
              value={textvalueObject}
              onChange={(e: any) => {
                onUrlTextHandler(e.target.value);
              }}
              validationError={errorMessage === 'Invalid' ? 'Invalid' : ''}
              validationSuccess={
                errorMessage &&
                (errorMessage === 'Valid' ||
                  errorMessage === 'Validated by policy')
                  ? 'Valid'
                  : ''
              }
            />
            {textvalueObject?.length === 1000 && (
              <p className="mt-1 inline-flex items-start gap-1 text-xs text-red-600 dark:text-red-400 md:text-sm">
                <ExclamationTriangleIcon
                  className="relative -bottom-[2px] -ml-0.5 h-5 w-5 shrink-0"
                  aria-hidden="true"
                />
                1000 character limit reached.
              </p>
            )}
          </div>

          <Button
            variant="secondary"
            onClick={() => {
              onHandleTestLink();
            }}
            disabled={textvalueObject === '' || btnState}
          >
            Test Link
          </Button>
        </p>
      ) : (
        <p className="w-4/6 px-3 py-2 text-sm">
          <InputMask
            id={`Text.${qnTempId}`}
            className={`${inputBaseClasses} ${inputSizeClasses.medium}  bg-white outline-none`}
            pattern={maskPattern}
            type={textContentType}
            min={questionDescription === 'Numbers Only' ? '0' : ''}
            onChange={(e: any) => {
              updateTextView(e.target.value, qnTempId);
            }}
            value={textvalueObject}
            isPhnNumber={
              questionDescription === 'Telephone (US)' ? true : false
            }
          />
        </p>
      )}
    </dd>
  );
}
